import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, CalendarDays, MapPin, Users, Loader2, Copy, ExternalLink, Link as LinkIcon } from "lucide-react";
import { eventsApi, formatAuDate, centsToAUD, coverImageUrl } from "../../lib/events";
import { api } from "../../lib/api";
import { WaitlistNudgeBanner } from "../../components/WaitlistNudgeBanner";
import OnboardingChecklist from "../../components/OnboardingChecklist";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const EMPTY_STATE_IMAGE =
  "https://images.unsplash.com/photo-1760170437237-a3654545ab4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxlbXB0eSUyMGF1ZGl0b3JpdW0lMjBzdGFnZXxlbnwwfHx8fDE3ODMxMjQ0MDl8MA&ixlib=rb-4.1.0&q=85";

function StatusBadge({ status, visibility }) {
  const label = status === "published" ? "Published" : status === "draft" ? "Draft" : status;
  const cls =
    status === "published"
      ? "bg-[var(--gt-ink)] text-white"
      : "bg-[var(--gt-bg-surface)] text-[var(--gt-ink)] border border-[var(--gt-line)]";
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span
        className={`inline-block text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-1 rounded-sm ${cls}`}
        data-testid={`event-status-${status}`}
      >
        {label}
      </span>
      {visibility === "private" && (
        <span
          className="inline-block text-[10px] font-bold tracking-[0.18em] uppercase px-2 py-1 rounded-sm bg-[var(--gt-accent)]/10 text-[var(--gt-accent)]"
          data-testid="event-visibility-private"
        >
          Private
        </span>
      )}
    </div>
  );
}

function EventCard({ e, onDuplicate, onShared }) {
  const s = e.ticket_types_summary || { count: 0, total_quantity: 0, total_sold: 0 };
  const cover = coverImageUrl(e);
  const isPublicPublished = e.status === "published" && e.visibility === "public";
  const publicHref = e.visibility === "private" && e.private_url_key
    ? `/e/private/${e.private_url_key}`
    : `/e/${e.slug}`;
  const publicAbsUrl = `${window.location.origin}${publicHref}`;
  const navigate = useNavigate();
  const [dupBusy, setDupBusy] = useState(false);

  const handleDuplicate = async (ev) => {
    ev.stopPropagation();
    if (dupBusy) return;
    setDupBusy(true);
    try {
      const dup = await eventsApi.duplicate(e.id);
      toast.success(`Duplicated as "${dup.title}"`);
      onDuplicate?.(dup);
      navigate(`/dashboard/events/${dup.id}/edit`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not duplicate this event.");
    } finally {
      setDupBusy(false);
    }
  };

  const handleCopyLink = async (ev) => {
    ev.stopPropagation();
    try {
      await navigator.clipboard.writeText(publicAbsUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link. Please copy from the address bar.");
    }
    // Sprint AJ — server owns the timestamp; we only ask it to set once.
    onShared?.();
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/dashboard/events/${e.id}/edit`)}
      onKeyDown={(ev) => {
        if (ev.key === "Enter" || ev.key === " ") {
          ev.preventDefault();
          navigate(`/dashboard/events/${e.id}/edit`);
        }
      }}
      className="group grid grid-cols-1 sm:grid-cols-[160px_1fr_auto] border border-[var(--gt-line)] rounded-sm hover:border-[var(--gt-ink)] transition-colors bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--gt-ink)]"
      data-testid={`event-card-${e.id}`}
    >
      <div className="relative w-full sm:w-[160px] aspect-[16/9] sm:aspect-auto sm:h-full bg-[var(--gt-bg-surface)] overflow-hidden">
        {cover ? (
          <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[var(--gt-ink-3)]">
            <CalendarDays size={28} strokeWidth={1.5} />
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col gap-2">
        <StatusBadge status={e.status} visibility={e.visibility} />
        <h3 className="font-heading text-xl font-bold tracking-tight leading-tight group-hover:text-[var(--gt-accent)] transition-colors">
          {e.title}
        </h3>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--gt-ink-2)]">
          <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} /> {formatAuDate(e.start_at)}</span>
          <span className="inline-flex items-center gap-1.5"><MapPin size={13} /> {e.venue_name}, {e.state}</span>
          <span className="inline-flex items-center gap-1.5"><Users size={13} /> {s.total_sold}/{s.total_quantity} sold · {s.count} tier{s.count===1?"":"s"}</span>
        </div>
      </div>
      <div className="p-5 sm:pr-6 flex sm:flex-col items-start sm:items-end justify-end gap-2 border-t sm:border-t-0 sm:border-l border-[var(--gt-line)]">
        <span className="font-mono text-xs uppercase tracking-widest text-[var(--gt-ink-3)]">
          {e.pricing_mode === "free" ? "FREE" : "PAID · AUD"}
        </span>
        <button
          type="button"
          onClick={(ev) => { ev.stopPropagation(); navigate(`/dashboard/events/${e.id}/team`); }}
          className="text-xs text-[var(--gt-ink-2)] hover:text-[var(--gt-ink)] inline-flex items-center gap-1"
          data-testid={`event-team-${e.id}`}
        >
          <Users size={11} /> Team
        </button>
        <button
          type="button"
          onClick={handleDuplicate}
          disabled={dupBusy}
          className="text-xs text-[var(--gt-ink-2)] hover:text-[var(--gt-ink)] inline-flex items-center gap-1 disabled:opacity-60"
          data-testid={`event-duplicate-${e.id}`}
          aria-label={`Duplicate ${e.title}`}
        >
          {dupBusy ? <Loader2 size={11} className="animate-spin" /> : <Copy size={11} />} Duplicate
        </button>
        {e.status === "published" && (
          <button
            type="button"
            onClick={(ev) => {
              ev.stopPropagation();
              window.open(publicHref, "_blank", "noopener,noreferrer");
            }}
            className="text-xs text-[var(--gt-ink-2)] hover:text-[var(--gt-ink)] inline-flex items-center gap-1"
            data-testid={`event-public-link-${e.id}`}
          >
            View <ExternalLink size={11} />
          </button>
        )}
        {isPublicPublished && (
          <button
            type="button"
            onClick={handleCopyLink}
            className="text-xs text-[var(--gt-ink-2)] hover:text-[var(--gt-ink)] inline-flex items-center gap-1"
            data-testid={`event-copy-link-${e.id}`}
            aria-label={`Copy public link for ${e.title}`}
          >
            <LinkIcon size={11} /> Copy link
          </button>
        )}
      </div>
    </div>
  );
}

export default function MyEvents() {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const [events, setEvents] = useState(null);
  const [error, setError] = useState("");
  const [nudges, setNudges] = useState({});

  const markShared = async () => {
    if (user?.event_shared_at) return; // already set — server ignores duplicates too
    try {
      await updateProfile({ mark_shared: true });
    } catch {
      /* silent — the toast for the copy itself is enough */
    }
  };

  const jumpToShareOnFirstPublicEvent = () => {
    const first =
      Array.isArray(events) &&
      events.find((e) => e.status === "published" && e.visibility === "public");
    if (!first) return;
    const el = document.querySelector(`[data-testid="event-copy-link-${first.id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus?.();
      el.click();
    }
  };

  useEffect(() => {
    let alive = true;
    eventsApi
      .listCached()
      .then((data) => alive && setEvents(data))
      .catch((e) => alive && setError(e?.response?.data?.detail || "Could not load events."));
    api.get("/admin/waitlist-nudges")
      .then((r) => {
        if (!alive) return;
        const map = {};
        (r.data.nudges || []).forEach((n) => { map[n.event_id] = n; });
        setNudges(map);
      })
      .catch(() => { /* nudges are advisory, non-critical */ });
    return () => { alive = false; };
  }, []);

  const isLoading = events === null && !error;
  const isEmpty = events && events.length === 0;

  return (
    <section className="p-6 sm:p-10" data-testid="my-events-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <div className="gt-label mb-2">Events</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">
              Your events
            </h2>
            <p className="text-[var(--gt-ink-2)] text-sm mt-2">
              Manage upcoming and past events. All prices in AUD, dates in DD/MM/YYYY.
            </p>
          </div>
          <button
            onClick={() => navigate("/dashboard/events/new")}
            className="gt-btn-primary"
            data-testid="create-event-btn"
          >
            <Plus size={16} strokeWidth={2.5} />
            Create event
          </button>
        </div>

        {/* Sprint AJ — Onboarding checklist (auto-hides when complete). */}
        <OnboardingChecklist
          user={user}
          events={events || []}
          onShareCtaClick={jumpToShareOnFirstPublicEvent}
        />

        {isLoading && (
          <div className="flex items-center gap-3 text-[var(--gt-ink-2)]" data-testid="events-loading">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">Loading your events…</span>
          </div>
        )}

        {error && (
          <div className="gt-card border-[var(--gt-accent)] text-[var(--gt-accent)]" data-testid="events-error">
            {error}
          </div>
        )}

        {isEmpty && (
          <div
            className="border border-[var(--gt-line)] rounded-sm overflow-hidden"
            data-testid="my-events-empty"
          >
            <div className="relative aspect-[16/8] bg-[var(--gt-bg-surface)]">
              <img
                src={EMPTY_STATE_IMAGE}
                alt="An empty auditorium waiting for its next event"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
            </div>
            <div className="p-8 sm:p-12 text-center max-w-lg mx-auto">
              <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-2">
                You have not created any events yet.
              </h3>
              <p className="text-[var(--gt-ink-2)] text-sm leading-relaxed mb-6">
                Your stage is set. Create your first event and start selling tickets across
                Australia today.
              </p>
              <button
                onClick={() => navigate("/dashboard/events/new")}
                className="gt-btn-primary"
                data-testid="empty-create-event-btn"
              >
                <Plus size={16} strokeWidth={2.5} />
                Create event
              </button>
            </div>
          </div>
        )}

        {events && events.length > 0 && (
          <div className="flex flex-col gap-4" data-testid="events-list">
            {events.filter((e) => !e.team_role).map((e) => (
              <div key={e.id} className="flex flex-col gap-1.5">
                <EventCard
                  e={e}
                  onDuplicate={(dup) => setEvents((prev) => (prev ? [dup, ...prev] : prev))}
                  onShared={markShared}
                />
                {nudges[e.id] && (
                  <WaitlistNudgeBanner
                    nudge={nudges[e.id]}
                    eventId={e.id}
                    variant="compact"
                    onDismissed={() => setNudges((prev) => { const n = { ...prev }; delete n[e.id]; return n; })}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Sprint AP — Events I Manage (team access) */}
        {events && events.some((e) => e.team_role) && (
          <div className="mt-12" data-testid="events-i-manage">
            <div className="gt-label mb-2">Events I Manage</div>
            <p className="text-[var(--gt-ink-2)] text-xs mb-4">
              Events you've been given team access to. Your access is limited to your assigned role.
            </p>
            <div className="flex flex-col gap-3">
              {events.filter((e) => e.team_role).map((e) => {
                const perms = e.team_permissions || [];
                return (
                  <div key={e.id} className="gt-card flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`managed-event-${e.id}`}>
                    <div>
                      <h3 className="font-heading text-lg font-bold tracking-tight">{e.title}</h3>
                      <div className="text-xs text-[var(--gt-ink-2)] mt-0.5">
                        {formatAuDate(e.start_at)} · {e.venue_name}, {e.state}
                      </div>
                      <span className="inline-block mt-2 text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 bg-[var(--gt-ink)] text-white rounded-sm" data-testid={`managed-role-${e.id}`}>
                        Role: {e.team_role_label || e.team_role}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      {perms.includes("event.edit") && (
                        <button onClick={() => navigate(`/dashboard/events/${e.id}/edit`)} className="gt-btn-ghost text-xs" data-testid={`managed-edit-${e.id}`}>Edit</button>
                      )}
                      {perms.includes("sales.view_dashboard") && (
                        <button onClick={() => navigate(`/dashboard/events/${e.id}/reports`)} className="gt-btn-ghost text-xs" data-testid={`managed-reports-${e.id}`}>Reports</button>
                      )}
                      {perms.includes("checkin.scanner") && (
                        <button onClick={() => navigate(`/dashboard/events/${e.id}/scanners`)} className="gt-btn-ghost text-xs" data-testid={`managed-scanners-${e.id}`}>Scanners</button>
                      )}
                      {perms.includes("team.view") && (
                        <button onClick={() => navigate(`/dashboard/events/${e.id}/team`)} className="gt-btn-ghost text-xs" data-testid={`managed-team-${e.id}`}>Team</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
